import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GlobalSearchBar from "@/components/feature/GlobalSearchBar";
import LanguageSwitcher from "@/components/base/LanguageSwitcher";

const LOGO_URL = "https://storage.readdy-site.link/project_files/5c942cfb-1ccb-4a1f-ae9b-6223a6645081/db57ada8-027e-48ac-9357-a295d5e6a211_wazi_stories_transparent.png?v=b6e6edf7454a9eb40d503dba8ed8f73a";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const primaryLinks = [
    { labelKey: "nav.successStories", path: "/success-stories" },
    { labelKey: "nav.startups", path: "/startups" },
    { labelKey: "nav.innovations", path: "/innovations" },
    { labelKey: "nav.femmes", path: "/femmes-entrepreneures" },
    { labelKey: "nav.diaspora", path: "/diaspora-africaine" },
  ];

  const moreLinks = [
    { labelKey: "nav.jeunesTalents", path: "/jeunes-talents", icon: "ri-star-line" },
    { labelKey: "nav.interviews", path: "/interviews", icon: "ri-play-circle-line" },
    { labelKey: "nav.opportunites", path: "/opportunites", icon: "ri-compass-3-line" },
    { labelKey: "nav.formations", path: "/formations", icon: "ri-graduation-cap-line" },
    { labelKey: "nav.apropos", path: "/a-propos", icon: "ri-information-line" },
  ];

  const allMobileLinks = [...primaryLinks, ...moreLinks];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setSearchOpen(false); setMoreOpen(false); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isMoreActive = moreLinks.some((l) => location.pathname === l.path);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="Wazi Stories Afrique"
              className="h-14 md:h-16 w-auto object-contain"
              style={{ maxWidth: "200px" }}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {primaryLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname === link.path
                    ? "text-orange-500 bg-orange-50"
                    : scrolled
                    ? "text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                    : "text-white hover:text-orange-300"
                }`}
              >
                {t(link.labelKey)}
              </Link>
            ))}

            {/* "Plus" dropdown */}
            <div ref={moreRef} className="relative">
              <button
                onClick={() => setMoreOpen((v) => !v)}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  isMoreActive
                    ? "text-orange-500 bg-orange-50"
                    : scrolled
                    ? "text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                    : "text-white hover:text-orange-300"
                }`}
              >
                {t("nav.more")}
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${moreOpen ? "rotate-180" : ""}`}></i>
              </button>

              {moreOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-2xl border border-gray-100 py-2 z-50" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.10)" }}>
                  {moreLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                        location.pathname === link.path
                          ? "text-orange-500 bg-orange-50"
                          : "text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                      }`}
                    >
                      <i className={`${link.icon} text-orange-400 text-base`}></i>
                      {t(link.labelKey)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-2">
            <LanguageSwitcher scrolled={scrolled} />
            <button
              onClick={() => setSearchOpen(true)}
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                scrolled
                  ? "bg-gray-100 hover:bg-orange-50 text-gray-600 hover:text-orange-500"
                  : "bg-white/20 hover:bg-white/30 text-white"
              }`}
              title={t("nav.search")}
            >
              <i className="ri-search-line text-base"></i>
            </button>
          </div>

          {/* Mobile right */}
          <div className="flex lg:hidden items-center gap-1">
            <LanguageSwitcher scrolled={scrolled} compact />
            <button
              onClick={() => setSearchOpen(true)}
              className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer ${scrolled ? "text-gray-700" : "text-white"}`}
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <button
              className={`w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${scrolled ? "text-gray-700" : "text-white"}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-4 flex flex-col gap-1">
              {allMobileLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-2 ${
                    location.pathname === link.path
                      ? "text-orange-500 bg-orange-50"
                      : "text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  }`}
                >
                  {"icon" in link && <i className={`${(link as { icon: string }).icon} text-orange-400 text-sm`}></i>}
                  {t(link.labelKey)}
                </Link>
              ))}
              <div className="pt-3 border-t border-gray-100 flex flex-col gap-2 mt-1">
                <Link
                  to="/recherche"
                  className="px-4 py-3 text-sm font-medium rounded-lg text-gray-700 hover:text-orange-500 hover:bg-orange-50 transition-colors cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-search-line text-orange-400 text-sm"></i>
                  {t("nav.searchGlobal")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSearchOpen(false)}></div>
          <div className="relative z-10 bg-white shadow-xl">
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-5 flex items-center gap-4">
              <div className="flex-1">
                <GlobalSearchBar autoFocus onClose={() => setSearchOpen(false)} placeholder={t("nav.searchPlaceholder")} />
              </div>
              <button onClick={() => setSearchOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="max-w-3xl mx-auto px-4 md:px-8 pb-4 flex flex-wrap gap-2">
              <span className="text-xs text-gray-400 self-center">{t("nav.popular")}</span>
              {["Fintech", "Femmes", "Diaspora", "Kenya", "Nigeria", "Innovation"].map((term) => (
                <Link key={term} to={`/recherche?q=${encodeURIComponent(term)}`} onClick={() => setSearchOpen(false)} className="text-xs bg-orange-50 text-orange-600 hover:bg-orange-100 px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                  {term}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
